import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { IApiResponse } from "../../../utils/types/api-response";
import {
    CONSENT_LINK_QUERY_KEY,
    consentLinkQueryKeys,
} from "./consent-link.queries";
import {
    addScopedPaymentCard,
    recordScopedConsentAction,
    saveScopedConsentDraft,
    uploadScopedConsentAttachments,
} from "../services/consent-link.services";
import {
    ConsentLinkAddPaymentCardRequest,
    ConsentLinkAddPaymentCardResponse,
    IConsentActionPayload,
    IConsentDraftPayload,
    IConsentFormRecord,
} from "../types/consent-link.types";

export const consentLinkMutationKeys = {
    action: (consentId: string) => [
        CONSENT_LINK_QUERY_KEY,
        consentId,
        "action",
    ],
    addPaymentCard: (consentId: string) => [
        CONSENT_LINK_QUERY_KEY,
        consentId,
        "add-payment-card",
    ],
    uploadAttachment: (consentId: string) => [
        CONSENT_LINK_QUERY_KEY,
        consentId,
        "upload-attachment",
    ],
};

export function useScopedConsentDraft(consentId: string) {
    const queryClient = useQueryClient();

    return useMutation<unknown, AxiosError, IConsentDraftPayload>({
        mutationFn: saveScopedConsentDraft,
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: consentLinkQueryKeys.consent(consentId),
            }),
    });
}

export function useScopedConsentAction(consentId: string) {
    const queryClient = useQueryClient();

    return useMutation<
        IApiResponse<IConsentFormRecord>,
        AxiosError,
        IConsentActionPayload
    >({
        mutationKey: consentLinkMutationKeys.action(consentId),
        mutationFn: recordScopedConsentAction,
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: consentLinkQueryKeys.consent(consentId),
            }),
    });
}

export function useScopedAddPaymentCard(consentId: string) {
    const queryClient = useQueryClient();

    return useMutation<
        ConsentLinkAddPaymentCardResponse,
        AxiosError,
        ConsentLinkAddPaymentCardRequest
    >({
        mutationKey: consentLinkMutationKeys.addPaymentCard(consentId),
        mutationFn: addScopedPaymentCard,
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: consentLinkQueryKeys.paymentCardStatus(consentId),
            }),
    });
}

export function useScopedConsentAttachments(consentId: string) {
    const queryClient = useQueryClient();

    return useMutation<
        IApiResponse<unknown>,
        AxiosError<{ message?: string }>,
        { fieldId: string; files: File[] }
    >({
        mutationKey: consentLinkMutationKeys.uploadAttachment(consentId),
        mutationFn: ({ fieldId, files }) =>
            uploadScopedConsentAttachments(fieldId, files),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: consentLinkQueryKeys.consent(consentId),
            }),
    });
}
